import { readSSE } from './stream.js';
import { MC } from '../utils/constants.js';

export class ApiClient {
  constructor(cfg, sessionData, bus, analytics = null, getToken = null) {
    this._cfg       = cfg;
    this._sess      = sessionData;
    this._bus       = bus;
    this._analytics = analytics;
    this._ctrl      = null;
    this._getToken  = getToken || (() => null);
  }

  _authHeaders() {
    const token = this._getToken();
    return token ? { 'X-Widget-Token': token } : {};
  }

  async send(message, { onStart, onChunk, onToolStart, onTool, onToolError, onConfirm, onSources, onDone, onError } = {}) {
    if (this._ctrl) this._ctrl.abort();
    this._ctrl = new AbortController();

    const mcpToken = document.querySelector('meta[name="mcp-auth"]')?.getAttribute('content') || null;

    const payload = {
      app_id:     this._cfg.appId,
      session_id: this._sess.sessionId,
      message,
      stream:     this._cfg.stream,
      ...(mcpToken !== null && { mcp_token: mcpToken }),
      user_info: {
        external_user_id: this._sess.userId           || null,
        name:             this._sess.userName         || null,
        email:            this._sess.userEmail        || null,
        business_name:    this._sess.userBusinessName || null,
        extra_metadata:   Object.keys(this._cfg.metadata || {}).length
                            ? { ...this._cfg.metadata, widget: MC.VERSION }
                            : { widget: MC.VERSION },
      },
      session_context: {
        entry_url:   this._sess.entryUrl  || null,
        exit_url:    null,
        device_type: this._analytics?.deviceType || null,
        browser:     this._analytics?.browser    || null,
        extra_metadata: null,
      },
    };

    try {
      let res = await fetch(`${this._cfg.apiUrl}/chat`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...this._authHeaders() },
        body:    JSON.stringify(payload),
        signal:  this._ctrl.signal,
      });

      if (res.status === 403 && this._getToken()) {
        // Token rejected — attempt one refresh via the widget-auth endpoint
        await this._refreshToken();
        res = await fetch(`${this._cfg.apiUrl}/chat`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', ...this._authHeaders() },
          body:    JSON.stringify(payload),
          signal:  this._ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw Object.assign(new Error(err.error || res.statusText), { status: res.status });
        }
      } else if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error(err.error || res.statusText), { status: res.status });
      }

      if (this._cfg.stream && res.headers.get('content-type')?.includes('event-stream')) {
        await readSSE(res, { onStart, onChunk, onToolStart, onTool, onToolError, onConfirm, onSources, onDone, onError });
      } else {
        const data = await res.json();
        if (data.sources?.length) onSources?.(data.sources);
        onDone?.({ reply: data.reply, tokens: data.token_usage, sources: data.sources || [] });
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      this._bus.emit('error', { error: e.message, status: e.status || 0 });
      onError?.(e);
    }
  }

  async _refreshToken() {
    try {
      const res = await fetch(`${this._cfg.apiUrl}/widget-auth/${encodeURIComponent(this._cfg.appId)}/token`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      // Update the shared token store through the getToken closure reference
      if (typeof window.__mcWidgetTokenStore !== 'undefined') {
        window.__mcWidgetTokenStore.token     = data.token || null;
        window.__mcWidgetTokenStore.expiresAt = Date.now() + ((data.expires_in || 86400) - 300) * 1000;
      }
    } catch {}
  }

  async createSession() {
    const sess = this._sess;
    const body = {
      session_id: sess.sessionId,
      app_id:     this._cfg.appId,
      user_info: {
        external_user_id: sess.userId           || null,
        name:             sess.userName         || null,
        email:            sess.userEmail        || null,
        business_name:    sess.userBusinessName || null,
      },
      session_context: {
        entry_url:   sess.entryUrl || null,
        device_type: this._analytics?.deviceType || null,
        browser:     this._analytics?.browser    || null,
      },
    };
    const res = await fetch(`${this._cfg.apiUrl}/chat/session/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this._authHeaders() },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`session/create ${res.status}`);
    return res.json();
  }

  async fetchHistory(sessionId) {
    const res = await fetch(
      `${this._cfg.apiUrl}/chat/history/${encodeURIComponent(sessionId)}?app_id=${encodeURIComponent(this._cfg.appId)}`,
      { method: 'GET', headers: { ...this._authHeaders() } }
    );
    if (!res.ok) throw new Error(`history ${res.status}`);
    return res.json();
  }

  async fetchSessions() {
    const userId = this._sess.userId || '';
    const res = await fetch(
      `${this._cfg.apiUrl}/chat/sessions?app_id=${encodeURIComponent(this._cfg.appId)}&user_id=${encodeURIComponent(userId)}`,
      { method: 'GET', headers: { ...this._authHeaders() } }
    );
    if (!res.ok) throw new Error(`sessions ${res.status}`);
    return res.json();
  }

  async submitReview(sessionId, rating, label, comment) {
    const res = await fetch(`${this._cfg.apiUrl}/chat/session/review`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...this._authHeaders() },
      body: JSON.stringify({ session_id: sessionId, app_id: this._cfg.appId, rating, emoji_label: label, comment: comment || null }),
    });
    if (!res.ok) throw new Error(`review ${res.status}`);
    return res.json();
  }

  abort() { this._ctrl?.abort(); this._ctrl = null; }
}
